import React, { useState, useRef, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { prefetchDetector } from '../lib/faceDetect';
import { resolveModelAssetBase } from '../lib/assetBase';
import type { GuideRect } from '../lib/faceAlign';

export type SelfieSource = 'camera' | 'upload';
export interface SelfieMeta {
  source: SelfieSource;
  /** camera guide-oval rect in captured-image px (camera path only) */
  guideRect?: GuideRect;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

// Measure the guide oval in the captured frame's intrinsic px. The preview is
// mirrored (scaleX(-1)) and object-fit:cover; the captured canvas is the
// UN-mirrored intrinsic frame, so we map through cover and flip x.
function captureGuideRect(
  video: HTMLVideoElement,
  oval: HTMLElement,
): GuideRect | undefined {
  try {
    const vb = video.getBoundingClientRect();
    const ob = oval.getBoundingClientRect();
    const Vw = video.videoWidth;
    const Vh = video.videoHeight;
    if (!Vw || !Vh || !vb.width || !vb.height) return undefined;
    const scale = Math.max(vb.width / Vw, vb.height / Vh);
    const originX = vb.left + (vb.width - Vw * scale) / 2;
    const originY = vb.top + (vb.height - Vh * scale) / 2;
    const ix = (ob.left - originX) / scale;
    const iy = (ob.top - originY) / scale;
    const iw = ob.width / scale;
    const ih = ob.height / scale;
    const ixUnmirrored = Vw - (ix + iw);
    return {
      x: clamp(ixUnmirrored, 0, Vw),
      y: clamp(iy, 0, Vh),
      w: clamp(iw, 1, Vw),
      h: clamp(ih, 1, Vh),
    };
  } catch {
    return undefined;
  }
}

const BackChevron = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Designed capture glyph (◉) — ring + filled core
const CaptureGlyph = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="12" stroke="currentColor" strokeWidth="2" />
    <circle cx="14" cy="14" r="6" fill="currentColor" />
  </svg>
);

const UploadGlyph = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="16 10 12 6 8 10"></polyline>
    <line x1="12" y1="6" x2="12" y2="17"></line>
    <path d="M20 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2"></path>
  </svg>
);

interface Stage03SelfieCaptureProps {
  onAdvance: (photo: File | null, meta?: SelfieMeta) => void;
  onBack: () => void;
}

export const Stage03SelfieCapture = ({ onAdvance, onBack }: Stage03SelfieCaptureProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const photoRef = useRef<HTMLCanvasElement>(null);
  const ovalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const guideRectRef = useRef<GuideRect | undefined>(undefined);
  const cameraModeRef = useRef({ useCameraMode: true, hasCamera: false, photo: null as File | null });
  const [hasCamera, setHasCamera] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoSource, setPhotoSource] = useState<'camera' | 'upload' | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [useCameraMode, setUseCameraMode] = useState(true);
  const [cameraStatus, setCameraStatus] = useState('Waiting for camera access…');

  cameraModeRef.current = { useCameraMode, hasCamera, photo };

  // Warm the cosmetic face detector (module + wasm + model) the moment the user
  // enters the photo step, so Stage 4 has a warm detector. Non-blocking; any
  // failure (offline / CSP / no model) is swallowed and trips the fallback.
  useEffect(() => {
    try {
      prefetchDetector(resolveModelAssetBase());
    } catch {
      /* never block the flow over a cosmetic prefetch */
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraStatus('Waiting for camera access…');
    setIsCameraActive(false);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      const { useCameraMode: cam, hasCamera: hc, photo: p } = cameraModeRef.current;
      if (!cam || !hc || p) {
        mediaStream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.addEventListener('loadedmetadata', () => {
          videoRef.current?.play().catch(() => {});
          if (videoRef.current && videoRef.current.videoWidth > 0) {
            setIsCameraActive(true);
          }
        }, { once: true });
      }
      setCameraStatus('Camera ready');
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasCamera(false);
      setIsCameraActive(false);
      setUseCameraMode(false);
      setCameraStatus('Camera access denied — please upload a photo instead');
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setIsCameraActive(false);
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      const videoInputExists = devices.some(device => device.kind === 'videoinput');
      setHasCamera(videoInputExists);
      if (videoInputExists) {
        setUseCameraMode(true);
      } else {
        setUseCameraMode(false);
        setCameraStatus('No camera found — please upload a photo instead');
      }
    });
  }, []);

  useEffect(() => {
    const shouldRunCamera = useCameraMode && hasCamera && !photo;
    if (!shouldRunCamera) {
      stopCamera();
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    startCamera();

    return () => {
      stopCamera();
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [useCameraMode, hasCamera, photo, startCamera, stopCamera]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
    };
  }, [photoPreviewUrl]);

  const takePhoto = () => {
    if (videoRef.current && photoRef.current) {
      const video = videoRef.current;
      const canvas = photoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context?.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Record the guide-oval geometry for the deterministic no-ML camera crop.
      guideRectRef.current = ovalRef.current
        ? captureGuideRect(video, ovalRef.current)
        : undefined;

      canvas.toBlob((blob) => {
        if (blob) {
          const capturedFile = new File([blob], "selfie.jpg", { type: "image/jpeg" });
          setPhoto(capturedFile);
          setPhotoSource('camera');
          setPhotoPreviewUrl(URL.createObjectURL(capturedFile));
          streamRef.current?.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      }, 'image/jpeg');
    }
  };

  const acceptFile = (file: File) => {
    setUploadError(null);

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setUploadError('Please choose a JPG or PNG image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) { // 10 MB
      setUploadError('Image size must be under 10 MB.');
      return;
    }

    setPhoto(file);
    setPhotoSource('upload');
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      acceptFile(files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<Element>) => {
    e.preventDefault();
    setIsUploading(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      acceptFile(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<Element>) => {
    e.preventDefault();
    setIsUploading(true);
  };

  const handleDragLeave = (e: React.DragEvent<Element>) => {
    e.preventDefault();
    setIsUploading(false);
  };

  const removePhoto = () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhoto(null);
    setPhotoSource(null);
    setPhotoPreviewUrl(null);
    setUploadError(null);
    guideRectRef.current = undefined;
  };

  const handleAdvance = () => {
    const meta: SelfieMeta | undefined = photoSource
      ? { source: photoSource, guideRect: photoSource === 'camera' ? guideRectRef.current : undefined }
      : undefined;
    onAdvance(photo, meta);
  };

  const handleSkip = () => {
    removePhoto();
    onAdvance(null);
  };

  const showCamera = !photo && useCameraMode && hasCamera;
  const showUpload = !photo && !showCamera;

  const title = photo
    ? (photoSource === 'camera' ? t('photo.title.usePhoto') : t('photo.title.reading'))
    : t('photo.title.default');
  const sub = photo
    ? (photoSource === 'camera'
        ? t('photo.sub.camera')
        : t('photo.sub.upload'))
    : showCamera
      ? t('photo.sub.showCamera')
      : t('photo.sub.default');

  return (
    <div className="vc-frame stage-03-selfie-capture">
      <style>{`
        /* Stage 03 — stage-specific layout only.
           Shared chamber vocabulary (viewport, oval, corners, capture,
           scanpass, upload zone, seg) → widget-components.css */
        .stage-03-selfie-capture .vc-photo-footer {
          margin-top: auto;
          display: flex;
          flex-direction: column;
          gap: var(--sp-4);
          padding-top: var(--sp-4);
          padding-bottom: var(--sp-6);
        }
        .stage-03-selfie-capture .vc-photo-actions {
          margin-top: 0;
        }
      `}</style>

      <div className="vc-head">
        <button className="vc-head-btn" onClick={onBack} aria-label={t('photo.backAria')}>
          <BackChevron />
        </button>
        <span className="vc-steplabel">{t('photo.step')}</span>
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
      </div>

      <h2 className="vc-title vc-title--md" role="heading" aria-level={2}>{title}</h2>
      <p className="vc-sub">{sub}</p>

      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {cameraStatus}
      </div>

      {showCamera && (
        // Camera Active State
        <div className="vc-viewport">
          <video ref={videoRef} playsInline muted autoPlay></video>
          <canvas ref={photoRef} style={{ display: 'none' }}></canvas>
          <i className="vc-corner tl"></i><i className="vc-corner tr"></i>
          <i className="vc-corner bl"></i><i className="vc-corner br"></i>
          <div ref={ovalRef} className="vc-oval"></div>
          <div className="vc-oval-cue">
            {isCameraActive ? t('photo.cue.align') : t('photo.cue.allow')}
          </div>
        </div>
      )}

      {showUpload && (
        // Upload Drop Zone State
        <div className="vc-viewport vc-viewport--raised">
          <label
            htmlFor="selfie-upload"
            tabIndex={0}
            role="button"
            aria-label={t('photo.upload.aria')}
            className={`vc-upload-zone ${isUploading ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                fileInputRef.current?.click();
              }
            }}
          >
            <span className="big"><UploadGlyph /></span>
            <span className="t">{t('photo.upload.tap')}</span>
            <span className="s">{t('photo.upload.formats')}</span>
            <input
              id="selfie-upload"
              type="file"
              accept="image/*"
              capture="user"
              className="vc-file-input"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </label>
        </div>
      )}

      {photo && (
        // Photo held state — scan pass earns the instrument moment
        <div className="vc-viewport">
          {photoPreviewUrl && <img src={photoPreviewUrl} alt={t('photo.previewAlt')} />}
          <i className="vc-corner tl"></i><i className="vc-corner tr"></i>
          <i className="vc-corner bl"></i><i className="vc-corner br"></i>
          {photoSource === 'upload' && <div className="vc-scanpass vc-scanpass--sweep"></div>}
        </div>
      )}

      {uploadError && (
        <p role="alert" className="vc-help vc-help--error" style={{ textAlign: 'center', marginTop: 'var(--sp-3)' }}>
          {uploadError}
        </p>
      )}

      {showCamera && (
        <button
          className="vc-capture"
          onClick={takePhoto}
          disabled={!isCameraActive}
          aria-disabled={!isCameraActive}
          aria-label={t('photo.captureAria')}
        >
          <CaptureGlyph />
        </button>
      )}

      <div className="vc-photo-footer">
        {photo ? (
          <div className="vc-photo-actions">
            <button type="button" className="vc-btn vc-btn--primary" onClick={handleAdvance}>
              {t('common.continue')}
            </button>
            <button type="button" className="vc-textlink" onClick={removePhoto}>
              {photoSource === 'camera' ? t('photo.retake') : t('photo.chooseDifferent')}
            </button>
          </div>
        ) : (
          <>
            {hasCamera && (
              <div className="vc-seg" role="group" aria-label={t('photo.seg.aria')}>
                <button
                  type="button"
                  className={useCameraMode ? 'is-active' : ''}
                  onClick={() => setUseCameraMode(true)}
                  aria-pressed={useCameraMode}
                >
                  {t('photo.seg.camera')}
                </button>
                <button
                  type="button"
                  className={!useCameraMode ? 'is-active' : ''}
                  onClick={() => setUseCameraMode(false)}
                  aria-pressed={!useCameraMode}
                >
                  {t('photo.seg.upload')}
                </button>
              </div>
            )}
            <button
              className="vc-textlink"
              onClick={handleSkip}
              aria-label={t('photo.skipAria')}
            >
              {t('photo.skip')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
